// Types for userService - separate file to avoid cache issues

/**
 * User roles matching backend enum UserRole.
 *
 * Roles used in the emenda workflow:
 *   ADMIN     — full access
 *   ORCAMENTO — admissibilidade analysis
 *   SECRETARIA — demand analysis
 *   CONVENIOS  — documental analysis
 *
 * Roles used in the institution/user management:
 *   OPERADOR, ANALISTA, JURIDICO
 *
 * "Gestor da Parceria" não é mais um role próprio: é um atributo booleano
 * (`gestorParceria`) sobre usuários SECRETARIA — ver User.gestorParceria.
 */
export enum UserRole {
  ADMIN     = 'ADMIN',
  ORCAMENTO = 'ORCAMENTO',
  SECRETARIA = 'SECRETARIA',
  CONVENIOS  = 'CONVENIOS',
  // --- institution / user management ---
  OPERADOR  = 'OPERADOR',
  GESTOR_JURIDICO = 'GESTOR_JURIDICO',
  ANALISTA  = 'ANALISTA',
  JURIDICO  = 'JURIDICO',
  JURIDICO_AUXILIAR = 'JURIDICO_AUXILIAR',
  // --- Câmara Municipal ---
  CAMARA    = 'CAMARA',
  VEREADOR  = 'VEREADOR',
}

/**
 * User status matching backend enum UserStatus
 */
export enum UserStatus {
  ATIVO = 'ATIVO',
  INATIVO = 'INATIVO',
  BLOQUEADO = 'BLOQUEADO',
  PENDENTE = 'PENDENTE'
}

/**
 * User entity with role-based access control fields
 */
export interface User {
  id: number;
  username: string;
  email: string;
  name: string;
  role?: UserRole;
  status?: UserStatus;
  instituicoes?: string[];
  secretaria?: string;
  documento?: string;
  parlamentarId?: string | null;
  procuradorSupervisorId?: number | null;
  /** Gestor da Parceria — atribuicao booleana, so valida quando role = SECRETARIA */
  gestorParceria?: boolean;
  /** Operador de secretaria — OPERADOR que atua em nome de uma secretaria (nome em `secretaria`) */
  operadorSecretaria?: boolean;
}

// Explicit export to ensure module recognition
export type { User as UserType };

/**
 * Permissions/actions available in the system
 */
export enum Permission {
  // Dashboard

  // Emendas
  VIEW_EMENDAS = 'VIEW_EMENDAS',
  CREATE_EMENDA = 'CREATE_EMENDA',
  EDIT_EMENDA = 'EDIT_EMENDA',
  APPROVE_ADMISSIBILIDADE = 'APPROVE_ADMISSIBILIDADE',
  VIEW_EMENDAS_VISAO_GERAL = 'VIEW_EMENDAS_VISAO_GERAL',
  VIEW_PRE_PROJETOS = 'VIEW_PRE_PROJETOS',
  MANAGE_PRE_PROJETOS = 'MANAGE_PRE_PROJETOS',
  SELECT_PRE_PROJETO = 'SELECT_PRE_PROJETO',

  // Execução financeira
  VIEW_EXECUCAO_FINANCEIRA = 'VIEW_EXECUCAO_FINANCEIRA',
  REGISTER_MOVIMENTACAO_FINANCEIRA = 'REGISTER_MOVIMENTACAO_FINANCEIRA',
  ANULAR_MOVIMENTACAO_FINANCEIRA = 'ANULAR_MOVIMENTACAO_FINANCEIRA',

  // Instituições
  VIEW_INSTITUTIONS = 'VIEW_INSTITUTIONS',
  CREATE_INSTITUTION = 'CREATE_INSTITUTION',
  EDIT_INSTITUTION = 'EDIT_INSTITUTION',
  MANAGE_INSTITUTION_DOCS = 'MANAGE_INSTITUTION_DOCS',
  MANAGE_INSTITUTION_DIRIGENTES = 'MANAGE_INSTITUTION_DIRIGENTES',
  APPROVE_INSTITUTION = 'APPROVE_INSTITUTION',
  BLOCK_INSTITUTION = 'BLOCK_INSTITUTION',

  // Parlamentares
  VIEW_COUNCILORS = 'VIEW_COUNCILORS',
  CREATE_COUNCILOR = 'CREATE_COUNCILOR',
  EDIT_COUNCILOR = 'EDIT_COUNCILOR',
  DELETE_COUNCILOR = 'DELETE_COUNCILOR',

  // Users
  VIEW_USERS = 'VIEW_USERS',
  CREATE_USER = 'CREATE_USER',
  EDIT_USER = 'EDIT_USER',
  DELETE_USER = 'DELETE_USER',
  APPROVE_USER = 'APPROVE_USER',

  // Reports
  VIEW_EMENDA_REPORTS = 'VIEW_EMENDA_REPORTS',
  VIEW_INSTITUTION_REPORTS = 'VIEW_INSTITUTION_REPORTS',
  VIEW_PENDENCIAS_REPORTS = 'VIEW_PENDENCIAS_REPORTS',
  VIEW_PRE_PROJETO_REPORTS = 'VIEW_PRE_PROJETO_REPORTS',
  EXPORT_DATA = 'EXPORT_DATA',

  // Profile
  VIEW_PROFILE = 'VIEW_PROFILE',
  EDIT_PROFILE = 'EDIT_PROFILE',

  // Workflow actions
  VIEW_GESTAO_PLANOS = 'VIEW_GESTAO_PLANOS',
  CREATE_PLANO_TRABALHO = 'CREATE_PLANO_TRABALHO',
  VIEW_PRESTACAO_CONTAS = 'VIEW_PRESTACAO_CONTAS',
  MANAGE_COMISSOES = 'MANAGE_COMISSOES',
  INICIAR_ANALISE_INSTITUICAO = 'INICIAR_ANALISE_INSTITUICAO',
  WORKFLOW_DECISION_PLANO = 'WORKFLOW_DECISION_PLANO',
  ASSIGN_PROCURADOR = 'ASSIGN_PROCURADOR',
  ACCESS_FILA_JURIDICA = 'ACCESS_FILA_JURIDICA',
  UPLOAD_RETRO_ANEXO = 'UPLOAD_RETRO_ANEXO',

  // Limites parlamentares
  MANAGE_PARLAMENTAR_LIMITES = 'MANAGE_PARLAMENTAR_LIMITES',
  EDIT_PERFIL_PUBLICO_PARLAMENTAR = 'EDIT_PERFIL_PUBLICO_PARLAMENTAR',

  // Dotação orçamentária
  MANAGE_DOTACAO_ORCAMENTARIA = 'MANAGE_DOTACAO_ORCAMENTARIA',

  // Solicitações de alteração do parlamentar
  CREATE_SOLICITACAO_ALTERACAO = 'CREATE_SOLICITACAO_ALTERACAO',
  TRATAR_SOLICITACAO_ALTERACAO = 'TRATAR_SOLICITACAO_ALTERACAO',


  // LOA / crédito adicional que dá suporte à emenda (qualquer fase)
  EDIT_EMENDA_VINCULO_ORCAMENTARIO = 'EDIT_EMENDA_VINCULO_ORCAMENTARIO',

  // Publicar no portal anexos revisados da prestação de contas (TAEP D.3)
  PUBLICAR_EVIDENCIA_PRESTACAO = 'PUBLICAR_EVIDENCIA_PRESTACAO',

  // Cadastro de leis orçamentárias (LOA) por exercício (TAEP B.2)
  MANAGE_LEIS_ORCAMENTARIAS = 'MANAGE_LEIS_ORCAMENTARIAS',

  // Autoavaliacao de transparencia das emendas (TAEP)
  MANAGE_TAEP = 'MANAGE_TAEP',

  // Consultar (somente leitura) os limites anuais por parlamentar
  VIEW_PARLAMENTAR_LIMITES = 'VIEW_PARLAMENTAR_LIMITES',

  // Configuração dos tipos de documentos (formatos, tamanho, obrigatoriedade)
  MANAGE_TIPOS_DOCUMENTO = 'MANAGE_TIPOS_DOCUMENTO',

  // Fixas por perfil (segregação de função / dono de etapa do workflow — o
  // backend decide por role hardcoded, não por esta permissão; existem só
  // pra tela de admin mostrar quem é o dono. Ver PermissoesConfigPage.tsx
  // FIXED_PERMISSIONS e docs/mapa-permissoes.md seção 10).
  REGISTRAR_REPASSE_PRORROGACAO = 'REGISTRAR_REPASSE_PRORROGACAO',
  GERENCIAR_COMPROVANTE_PRESTACAO = 'GERENCIAR_COMPROVANTE_PRESTACAO',
  ETAPA_EMENDA_DEMANDA = 'ETAPA_EMENDA_DEMANDA',
  ETAPA_EMENDA_DOCUMENTAL = 'ETAPA_EMENDA_DOCUMENTAL',
  ETAPA_PLANO_ORCAMENTO = 'ETAPA_PLANO_ORCAMENTO',
  ETAPA_PLANO_CONVENIOS_ANALISE = 'ETAPA_PLANO_CONVENIOS_ANALISE',
  ETAPA_PLANO_GESTOR_PARCERIA = 'ETAPA_PLANO_GESTOR_PARCERIA',
  ETAPA_PLANO_COMISSAO_SELECAO = 'ETAPA_PLANO_COMISSAO_SELECAO',
  ETAPA_PLANO_PROCURADORIA_JURIDICA = 'ETAPA_PLANO_PROCURADORIA_JURIDICA',
  ETAPA_PLANO_GESTOR_JURIDICO_REVISAO = 'ETAPA_PLANO_GESTOR_JURIDICO_REVISAO',
  ETAPA_PLANO_CONVENIOS_MINUTA = 'ETAPA_PLANO_CONVENIOS_MINUTA',
  ETAPA_PLANO_CONVENIOS_FORMALIZACAO = 'ETAPA_PLANO_CONVENIOS_FORMALIZACAO',
}

/**
 * Route paths and their required permissions
 */
export const ROUTE_PERMISSIONS: Record<string, Permission[]> = {
  '/dashboard/emendas': [Permission.VIEW_EMENDAS],
  '/dashboard/emendas/visao-geral': [Permission.VIEW_EMENDAS_VISAO_GERAL],
  '/dashboard/cadastro-emenda': [Permission.CREATE_EMENDA],
  '/dashboard/plano-trabalho': [Permission.VIEW_GESTAO_PLANOS],
  '/dashboard/planos': [Permission.VIEW_GESTAO_PLANOS, Permission.CREATE_PLANO_TRABALHO],
  '/dashboard/gestao/planos-trabalho': [Permission.VIEW_GESTAO_PLANOS],
  '/dashboard/gestao/prestacao-contas-planos': [Permission.VIEW_PRESTACAO_CONTAS],
  '/dashboard/plano/prestacao-contas': [Permission.VIEW_PRESTACAO_CONTAS],
  '/dashboard/execucao-financeira': [Permission.VIEW_EXECUCAO_FINANCEIRA],
  '/dashboard/pre-projetos': [Permission.VIEW_PRE_PROJETOS],
  '/dashboard/instituicoes': [Permission.VIEW_INSTITUTIONS],
  '/dashboard/relatorios/emendas': [Permission.VIEW_EMENDA_REPORTS],
  '/dashboard/relatorios/instituicoes': [Permission.VIEW_INSTITUTION_REPORTS],
  '/dashboard/relatorios/pre-projetos': [Permission.VIEW_PRE_PROJETO_REPORTS],
  '/dashboard/relatorios/pendencias/gestores': [Permission.VIEW_PENDENCIAS_REPORTS],
  '/dashboard/relatorios/pendencias/comissoes': [Permission.VIEW_PENDENCIAS_REPORTS],
  '/dashboard/relatorios/pendencias/devolvidas': [Permission.VIEW_PENDENCIAS_REPORTS],
  '/dashboard/relatorios/pendencias/com-ciencia': [Permission.VIEW_PENDENCIAS_REPORTS],
  '/dashboard/relatorios/pendencias/secretarias-sem-plano': [Permission.VIEW_PENDENCIAS_REPORTS],
  '/dashboard/cadastro-dados-institucionais': [Permission.EDIT_INSTITUTION], // Changed from VIEW_INSTITUTIONS
  '/dashboard/cadastro-dirigente': [Permission.MANAGE_INSTITUTION_DIRIGENTES],
  '/dashboard/diretoria': [Permission.MANAGE_INSTITUTION_DIRIGENTES],
  '/dashboard/parlamentares': [Permission.VIEW_COUNCILORS],
  '/dashboard/usuarios': [Permission.VIEW_USERS],
  '/dashboard/profile': [Permission.VIEW_PROFILE],
  '/emendas': [Permission.VIEW_EMENDAS],
  '/painel/emendas': [Permission.VIEW_EMENDAS],
  '/dashboard/admin/parlamentar-limites': [Permission.MANAGE_PARLAMENTAR_LIMITES, Permission.VIEW_PARLAMENTAR_LIMITES],
  '/dashboard/taep': [Permission.MANAGE_TAEP],
  '/dashboard/tipos-documento-config': [Permission.MANAGE_TIPOS_DOCUMENTO],
  '/dashboard/leis-orcamentarias': [Permission.MANAGE_LEIS_ORCAMENTARIAS],
};


export interface InstituicaoDetalhada {
  id: string;
  razaoSocial: string;
  nomeFantasia?: string;
  cnpj: string;
  emailInstitucional: string;
  telefone: string;
  cidade: string;
  uf: string;
  dataVinculo: string;
  ativo: boolean;
  bloqueada?: boolean;
  motivoBloqueio?: string;
  statusOSC?: string;
  motivoReprovacao?: string;
}

export interface EmendaResumida {
  id: string;
  codigoOficial: string;
  valor: number;
  descricao: string;
  status: string;
  statusCicloVida?: string;
  tipoTransferencia?: string;
  instituicaoNome: string;
  parlamentarNome: string;
  dataCriacao: string;
  categoria: string;
}

export interface RegisterPayload {
  username: string;
  email: string;
  password: string;
}
